<script setup lang="ts">
import { toRef } from "vue";
import { TooltipContent, TooltipPrimitive, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCardData } from "@/composables/useCardData";
import { useChartTile } from "@/composables/useChartTile";
import type { ResultsCard } from "@/types";

const props = defineProps<{ card: ResultsCard }>();

const card = toRef(props, "card");
const { questionMeta, matrixPopulations } = useCardData(card);
const { tiles, tile: current, selectTile } = useChartTile(card, questionMeta, matrixPopulations);
</script>

<template>
  <TooltipProvider
    :delay-duration="0"
    :skip-delay-duration="0"
    disable-closing-trigger>
    <div class="grid grid-cols-5 gap-1">
      <TooltipPrimitive
        v-for="tile in tiles"
        :key="tile.value">
        <TooltipTrigger
          as-child
          class="flex min-w-0">
          <span
            class="flex min-w-0 select-none"
            :class="{ 'cursor-not-allowed': tile.disabled }">
            <button
              type="button"
              :aria-label="tile.label"
              :aria-pressed="current === tile.value"
              :disabled="tile.disabled"
              class="flex w-full flex-col items-center gap-1.5 rounded-md border px-1 py-2 text-3xs leading-none transition-colors disabled:pointer-events-none"
              :class="
                tile.disabled
                  ? 'border-dashed border-border bg-transparent text-muted-foreground/40'
                  : current === tile.value
                    ? 'border-primary/40 bg-primary/5 text-primary'
                    : 'border-transparent bg-muted/60 text-foreground/80 hover:bg-muted hover:text-foreground'
              "
              @click="selectTile(tile.value)">
              <component
                :is="tile.icon"
                class="size-5" />
              <span class="w-full truncate text-center">{{ tile.short }}</span>
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent class="text-xs">
          <p class="font-medium">{{ tile.label }}</p>
          <p
            v-if="tile.reason"
            class="mt-0.5 max-w-56 opacity-80">
            {{ tile.reason }}
          </p>
        </TooltipContent>
      </TooltipPrimitive>
    </div>
  </TooltipProvider>
</template>
